// wake-lock.js — Bildschirm wach halten, gemeinsam für Knoten-Spieler und Messwerkzeuge.
//
//   holdWakeLock({ win }) → { release() }
//
// In der App über die Brücke `wakeLock` (wakelock_plus). Kennt die App den Handler nicht (ältere Version) oder läuft
// die Seite im Browser, dann `navigator.wakeLock`: Der Browser gibt die Sperre beim Verbergen des Tabs frei, deshalb
// wird sie bei `visibilitychange` → visible neu angefordert. Abgesichert gegen das Rennen zweier gleichzeitiger
// Anforderungen (zwei Sperren, eine verwaist) und gegen eine Antwort, die erst nach release() ankommt.
import { nativeWakeLock, bridge } from './native.js';

export function holdWakeLock({ win = typeof window !== 'undefined' ? window : null } = {}) {
  const doc = win && win.document;
  let lock = null, pending = null, dead = false, mode = null, appOn = false; // mode: 'app' | 'web'

  function acquire() {
    if (dead || !doc || doc.visibilityState !== 'visible') return pending;
    if ((lock && !lock.released) || pending) return pending;
    const wl = win.navigator && win.navigator.wakeLock;
    if (!wl || typeof wl.request !== 'function') return null;
    pending = (async () => {
      try {
        const l = await wl.request('screen');
        if (!l) return;
        if (dead) { Promise.resolve(l.release()).catch(() => {}); return; }
        lock = l;
      } catch { /* nicht erlaubt (Akku sparen, kein Fokus) — dann eben nicht */ } finally { pending = null; }
    })();
    return pending;
  }
  const onVis = () => { if (mode === 'web' && doc.visibilityState === 'visible') acquire(); };

  function useWeb() {
    if (dead || !doc) return;
    mode = 'web';
    doc.addEventListener('visibilitychange', onVis);
    acquire();
  }

  if (win && bridge(win)) {
    mode = 'app';
    nativeWakeLock(true, win).then((ok) => {
      if (ok) { if (dead) nativeWakeLock(false, win); else appOn = true; return; }
      if (!dead) useWeb();
    });
  } else useWeb();

  return {
    release() {
      if (dead) return;
      dead = true;
      if (doc) doc.removeEventListener('visibilitychange', onVis);
      if (appOn) nativeWakeLock(false, win);
      if (lock && !lock.released) Promise.resolve(lock.release()).catch(() => {});
      lock = null;
    },
  };
}
